<template>
  <DialogRoot :open="open" @update:open="handleUpdateOpen">
    <DialogTrigger
      v-if="$slots.default"
      as="button"
      :class="props.class"
      :data-id="trackingId"
    >
      <slot />
    </DialogTrigger>
    <DialogContent
      :variant="variant"
      :background="background"
      :size="size"
      :side="_side"
      :icon="!!icon"
      :teleport="teleport"
      :dismissible="dismissible"
      :ariaProps="{
        'aria-describedby': description ? 'dialog-description' : undefined,
      }"
      :withBackdropButton="withBackdropButton"
      @close="handleUpdateOpen(false)"
    >
      <template #header>
        <slot name="header">
          <DialogHeader
            ref="header"
            :icon="icon"
            :title="title"
            :description="description"
            :theme="theme"
            :side="_side"
            :withSearch="withSearch"
            :withClose="withClose"
            :alignItems="alignItems"
          >
            <template #tabs>
              <slot name="tabs" />
            </template>
            <template #search="{ query }">
              <slot name="search" :query="query" />
            </template>
          </DialogHeader>
        </slot>
      </template>
      <slot name="content">
        <div class="h-4" />
      </slot>
      <template v-if="showFooter" #footer>
        <Transition name="fade-slow" mode="out-in">
          <slot name="footer">
            <DialogFooter
              :cancelLabel="cancelLabel"
              :confirmLabel="confirmLabel"
              :showCancel="showCancel"
              :showConfirm="showConfirm"
              :showClear="showClear"
              :confirmType="confirmType"
              :disableConfirm="disableConfirm"
              :closeOnCancel="closeOnCancel"
              :closeOnConfirm="closeOnConfirm"
              :closeOnClear="closeOnClear"
              :alignButtons="showClear ? 'end' : alignButtons"
              @cancel="$emit('cancel')"
              @confirm="$emit('confirm')"
              @clear="$emit('clear')"
            />
          </slot>
        </Transition>
      </template>
    </DialogContent>
  </DialogRoot>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import { storeToRefs } from "pinia";
import { DialogRoot, DialogTrigger } from "radix-vue";
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/SharedComponents/Dialog";
import { directionStore } from "@/Stores/direction";

const useDirectionStore = directionStore();
const { direction } = storeToRefs(useDirectionStore);

const emit = defineEmits(["update:open", "cancel", "confirm", "clear"]);

const header = ref(null);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  variant: { type: String, required: false },
  background: { type: String, required: false },
  theme: { type: String, required: false },
  size: { type: [String, Number], required: false },
  side: { type: String, required: false },
  icon: { type: String, required: false },
  title: { type: String, required: false },
  description: { type: String, required: false },
  dismissible: { type: Boolean, default: true },
  teleport: { type: Boolean, default: true },
  withSearch: { type: Boolean, default: false },
  withClose: { type: Boolean, default: true },
  showFooter: { type: Boolean, default: true },
  cancelLabel: { type: String, required: false },
  confirmLabel: { type: String, required: false },
  showCancel: { type: Boolean, default: true },
  showConfirm: { type: Boolean, default: true },
  showClear: { type: Boolean, default: false },
  disableConfirm: { type: Boolean, default: false },
  closeOnCancel: { type: Boolean, default: true },
  closeOnConfirm: { type: Boolean, default: true },
  closeOnClear: { type: Boolean, default: true },
  alignItems: { type: String, required: false },
  alignButtons: { type: String, required: false },
  class: { type: null, required: false },
  trackingId: { type: String, required: false },
  withBackdropButton: { type: Boolean, default: false },
});

const confirmType = computed(() => {
  switch (props.theme) {
    case "error":
      return "destructive";
    case "success":
      return "success";
    default:
      return undefined;
  }
});

const _side = computed(() => {
  if (direction.value === "rtl") {
    if (props.side === "right") {
      return "left";
    }
    if (props.side === "left") {
      return "right";
    }
  }
  return props.side;
});

function handleUpdateOpen(value) {
  setTimeout(() => emit("update:open", value), 100);
}

watchEffect(() => {
  open.value = props.open;
});

defineExpose({
  query: computed(() => header.value?.query),
  clearQuery: () => header.value?.clearQuery(),
  close: () => header.value?.close(),
});
</script>
